import { z } from 'zod';

/**
 * Wire schemas for notification API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Read status as integer (0/1) in D1 database, not boolean
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 * - Metadata is stored as JSON string in D1, parsed to object in response
 */

/**
 * Notification wire schema (matches backend response format)
 * Based on NotificationReadSchema from Gateway
 */
export const notificationWireSchema = z.object({
  id: z.string(),
  user_id: z.string(), // recipient
  actor_id: z.string().nullable().optional(), // who triggered (nullable for system notifications)
  type: z.enum(['like', 'comment', 'follow', 'mention', 'friend_request', 'friend_request_accepted', 'list_invitation', 'list_invitation_accepted', 'list_item_added', 'friend_recommendation']),
  content: z.string().min(1), // human-readable message
  reference_type: z.enum(['business', 'review', 'user', 'check_in', 'list', 'menu_item']).nullable().optional(),
  reference_id: z.string().nullable().optional(), // ID of referenced entity
  metadata: z.record(z.string(), z.any()).nullable().optional(), // JSON parsed from DB
  read: z.number().int().min(0).max(1).default(0), // 0=unread, 1=read
  created_at: z.string(), // D1 DATETIME (ISO format)
});

export type NotificationWire = z.infer<typeof notificationWireSchema>;

/**
 * Paginated notifications response schema
 */
export const paginatedNotificationsWireSchema = z.object({
  items: z.array(notificationWireSchema),
  total: z.number().int(),
  page: z.number().int(),
  per_page: z.number().int(),
  total_pages: z.number().int(),
});

export type PaginatedNotificationsWire = z.infer<typeof paginatedNotificationsWireSchema>;

/**
 * Unread count response schema
 */
export const unreadCountWireSchema = z.object({
  unread_count: z.number().int().min(0),
});

export type UnreadCountWire = z.infer<typeof unreadCountWireSchema>;
